/**
 * The quiet-path gate (03 §5.2): there must be a way from the drop band to the edge of the goal
 * zone that a careful ranger can take unseen - crawling, keeping to cover and to the ravines,
 * waiting for patrols and lamps to pass.
 *
 *  1. Static eyes (sentries, gunners) are collapsed in time: ground any of them can ever see a
 *     crawling man on is simply shut. A flood from the drop band must still reach the goal.
 *  2. Patrols and searchlights are simulated step by step over one loop each (model.ts) and
 *     written into a window of `QUIET.windowWords` x 32 steps per cell; `searchTimed` then looks
 *     for a route through that window, waiting in cover between passes (search.ts).
 *
 * Wire and mined ground are shut throughout, as they are for the connectivity gate.
 */

import type { MissionDef, Rect } from '../../../sim/types';
import { ARCHETYPES } from '../../archetypes';
import { QUIET } from '../../tuning';
import type { Band } from '../../types';
import type { NavProbe } from '../nav';
import { capturedLegs, type QuietLeg } from './legs';
import { garrisonOf, type Look, type Mover } from './model';
import { staticFault, timedFault } from './report';
import { flood, searchTimed } from './search';
import { SightIndex, trenchCells } from './sight';

export interface QuietPath {
  ok: boolean;
  /** One sentence naming the band where the route is cut; null when there is a route. */
  fault: string | null;
  /** Which search failed: `static` = shut by men who never move, `timed` = no gap between the passes. */
  stage: 'static' | 'timed' | null;
  /** Where a crawling ranger gets to, unseen, that is nearest the goal; null when he gets there. */
  stoppedAt: { x: number; z: number } | null;
  watchers: number;
  movers: number;
}

/** Where the quiet route has to get to: the goal zone's edge - or, with no compound (Diversion), the border strip. */
function goalRect(def: MissionDef, bands: readonly Band[]): Rect {
  const last = bands[bands.length - 1];
  if (ARCHETYPES[def.archetype].layout === 'border' && last?.kind === 'goal') {
    return { x0: 0, z0: 0, x1: def.sizeX, z1: last.z1 };
  }
  const g = def.goalZone;
  const m = QUIET.goalMargin;
  return { x0: g.x0 - m, z0: g.z0 - m, x1: g.x1 + m, z1: g.z1 + m };
}

/** The drop band as a rectangle: he chooses where in it he comes down. */
function dropRect(def: MissionDef): Rect {
  return { x0: 0, z0: def.dropBand.z0, x1: def.sizeX, z1: def.dropBand.z1 };
}

/**
 * Cells of a rectangle. Where a leg starts only cells wholly inside count (`outward` false); where it ends,
 * every cell the rectangle touches does.
 */
function cellsIn(rect: Rect, width: number, height: number, outward: boolean): number[] {
  const round = outward ? [Math.floor, Math.ceil] as const : [Math.ceil, Math.floor] as const;
  const [x0, x1] = [Math.max(0, round[0](rect.x0)), Math.min(width, round[1](rect.x1))];
  const cells: number[] = [];
  for (let z = Math.max(0, round[0](rect.z0)); z < Math.min(height, round[1](rect.z1)); z++) {
    for (let x = x0; x < x1; x++) cells.push(z * width + x);
  }
  return cells;
}

const sameHeading = (a: Look, b: Look): boolean => a.facing === b.facing && a.half === b.half && a.inTrench === b.inTrench;
const sameLook = (a: Look, b: Look): boolean => a.x === b.x && a.z === b.z && sameHeading(a, b);

/** Two steps of one leg, watched from half way between them with the range let out to cover both: half the looks. */
function between(a: Look, b: Look): Look {
  const reach = Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z)) / 2;
  return { ...a, x: (a.x + b.x) / 2, z: (a.z + b.z) / 2, range: a.range + reach, sense: a.sense + reach };
}

/** Write one mover's loop into the window: bit k of a cell is set when it is seen on step k. */
function writeMover(sight: SightIndex, m: Mover, wanted: Uint8Array, words: number, watched: Uint32Array): void {
  const period = m.looks.length;
  const horizon = words * 32;
  const mask = new Uint32Array(words);
  const seen: number[] = [];
  for (let k = 0; k < period;) {
    let look = m.looks[k];
    if (!look) break;
    // Steps on which nothing about him changes (a pause at a node) are one look; so are two steps of one leg.
    let end = k + 1;
    while (end < period && sameLook(look, m.looks[end] ?? look)) end++;
    const next = m.looks[end];
    if (end === k + 1 && next && sameHeading(look, next)) {
      look = between(look, next);
      end++;
    }
    mask.fill(0);
    for (let step = k; step < end; step++) {
      // A loop shorter than the window repeats in it; a longer one is folded back onto it.
      for (let t = step % horizon; t < horizon; t += period) {
        mask[t >>> 5] = ((mask[t >>> 5] ?? 0) | (1 << (t & 31))) >>> 0;
        if (period >= horizon) break;
      }
    }
    seen.length = 0;
    sight.visible(look, wanted, seen);
    for (const cell of seen) {
      const at = cell * words;
      for (let w = 0; w < words; w++) watched[at + w] = ((watched[at + w] ?? 0) | (mask[w] ?? 0)) >>> 0;
    }
    k = end;
  }
}

export function quietPath(def: MissionDef, nav: NavProbe, bands: readonly Band[]): QuietPath {
  const { width, height } = nav;
  const size = width * height;
  const passable = new Uint8Array(size);
  for (let i = 0; i < size; i++) passable[i] = nav.hard[i] === 0 && nav.soft[i] === 0 ? 1 : 0;
  const sight = new SightIndex(nav.colliders, width, height, trenchCells(def.trenches, width, height));
  const { watchers, movers } = garrisonOf(def, (x, z) => sight.inTrench(x, z));
  const counts = { watchers: watchers.length, movers: movers.length };

  // 1. The men who never move.
  const seenBy = new Int16Array(size).fill(-1);
  const cells: number[] = [];
  watchers.forEach((w, index) => {
    cells.length = 0;
    sight.visible(w, passable, cells);
    for (const c of cells) if (seenBy[c] === -1) seenBy[c] = index;
  });
  const open = new Uint8Array(size);
  for (let i = 0; i < size; i++) open[i] = passable[i] !== 0 && seenBy[i] === -1 ? 1 : 0;

  // One leg for a parachutist - the drop band to the goal zone's edge; a prisoner's legs are his own (legs.ts).
  const legs: QuietLeg[] = capturedLegs(def) ?? [{ from: dropRect(def), to: goalRect(def, bands) }];
  for (const leg of legs) {
    const sources = cellsIn(leg.from, width, height, false).filter((cell) => open[cell] !== 0);
    const goal = new Uint8Array(size);
    for (const cell of cellsIn(leg.to, width, height, true)) goal[cell] = 1;
    const still = flood(width, open, sources, goal);
    if (still.goal < 0) {
      const { fault, stop } = staticFault(bands, leg.to, width, still.reached, seenBy, watchers);
      return { ok: false, fault, stage: 'static', stoppedAt: { x: stop.x, z: stop.z }, ...counts };
    }

    // 2. The men who walk, and the lamps: only ground the first search could get to matters.
    const words = QUIET.windowWords;
    const watched = new Uint32Array(size * words);
    for (const m of movers) writeMover(sight, m, still.reached, words, watched);
    const timed = searchTimed(width, words, still.reached, watched, sources, goal);
    if (timed.goal >= 0) continue;
    const { fault, stop } = timedFault(bands, leg.to, width, timed.reached, movers);
    return { ok: false, fault, stage: 'timed', stoppedAt: { x: stop.x, z: stop.z }, ...counts };
  }
  return { ok: true, fault: null, stage: null, stoppedAt: null, ...counts };
}

export function checkQuietPath(def: MissionDef, nav: NavProbe, bands: readonly Band[]): string[] {
  const result = quietPath(def, nav, bands);
  return result.fault === null ? [] : [result.fault];
}
